/**
 * Thin wrapper over GA4's gtag.
 *
 * gtag is only loaded on the production hostnames (see public/index.html), so
 * on dev and localhost every call is a no-op that logs to the console
 * instead -- handy for checking events fire without polluting prod data.
 *
 * Privacy: never send emails, names, video titles or file names. Event
 * params are categories and numbers only. The GA user_id is our internal
 * UUID, which GA allows and which lets retention reports follow a signed-up
 * user across devices.
 */

const isDevHost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname.startsWith('dev.'));

const gtagFn = () => (typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null);

export const GA_MEASUREMENT_ID = 'G-2GQV4LHMZ2';

/** Fire a GA4 event. Safe to call anywhere; never throws. */
export function track(eventName, params = {}) {
  try {
    const gtag = gtagFn();
    if (gtag) {
      gtag('event', eventName, params);
    } else if (isDevHost()) {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', eventName, params);
    }
  } catch {
    // Analytics must never break the app.
  }
}

/** Attach (or clear, with null) the signed-up user's internal ID. */
export function setAnalyticsUser(userId) {
  try {
    const gtag = gtagFn();
    if (gtag) {
      gtag('config', GA_MEASUREMENT_ID, { user_id: userId || undefined, send_page_view: false });
      gtag('set', 'user_properties', { account_type: userId ? 'signed_up' : 'anonymous' });
    } else if (isDevHost()) {
      // eslint-disable-next-line no-console
      console.debug('[analytics] user', userId || '(anonymous)');
    }
  } catch {
    // ignore
  }
}

/** Coarse score bucket, so reports can compare "issues found" vs "clean". */
export const scoreBand = (score) => {
  if (score == null || Number.isNaN(Number(score))) return 'unknown';
  const s = Number(score);
  if (s >= 80) return 'clean';
  if (s > 40) return 'issues';
  return 'at_risk';
};
